import * as THREE from 'three';
import { ball, box, leaf, mesh, random } from './art';
import type { Place } from './data';
import { buildCanal, canalBlocked } from './canalArt';
import { CanalAnimals } from './canalAnimals';

export const DISTRICTS = [
  { id: 'flower-meadows', name: 'Flower Meadows', subtitle: 'New paths beyond the fountain', x: 216, z: 168, color: '#91b877', story: 'The garden opens into a broad meadow. Flower stems become towers and petals shelter little clearings.', minX: 144, maxX: 336, minZ: 96, maxZ: 240 },
  { id: 'market-street', name: 'Market Street', subtitle: 'Beyond the artist courtyard', x: 408, z: 24, color: '#c9a681', story: 'Crates and paper bags rise above the street. Multiple open lanes lead between forgotten fruit and tiny treasures.', minX: 336, maxX: 528, minZ: -48, maxZ: 96 },
  { id: 'rainwater-canal', name: 'Rainwater Canal', subtitle: 'Bicycles beside the quiet water', x: 360, z: -120, color: '#77aeb0', story: 'Amsterdam-inspired paved quays, flower boxes and parked bicycles frame the water. Moored boats rest between eight stone crossings with curved iron railings.', minX: 144, maxX: 528, minZ: -192, maxZ: -48 },
] satisfies (Place & { minX: number; maxX: number; minZ: number; maxZ: number })[];
export const FRONTIER_SECRETS = [
  { id: 'meadow-dew', name: 'The dew mirror', x: 216, z: 192 },
  { id: 'meadow-petal', name: 'A petal shelter', x: 264, z: 168 },
  { id: 'meadow-pollen', name: 'Golden pollen', x: 312, z: 216 },
  { id: 'market-peach', name: 'The peach mountain', x: 408, z: 72 },
  { id: 'market-bag', name: 'The paper bag hollow', x: 456, z: 24 },
  { id: 'market-button', name: 'A market-stall button', x: 504, z: 72 },
  { id: 'canal-shell', name: 'The rain-polished shell', x: 168, z: -168 },
  { id: 'canal-bridge', name: 'The little canal bridge', x: 264, z: -120 },
  { id: 'canal-reeds', name: 'A whisper in the reeds', x: 408, z: -168 },
];
export function districtAt(x: number, z: number) { return DISTRICTS.find(d => x >= d.minX && x <= d.maxX && z >= d.minZ && z <= d.maxZ); }
export function frontierCell(cx: number, cz: number): boolean { return !!districtAt(cx * 48 + 24, cz * 48 + 24); }
export const CANAL_BRIDGES = Array.from({ length: 8 }, (_, i) => 168 + i * 48);
export function onCanalBridge(x: number, z: number): boolean { return x >= 144 && x <= 528 && Math.abs(z + 120) < 7 && CANAL_BRIDGES.some(a => Math.abs(x - a) < 2.4); }
export function frontierBlocked(x: number, z: number, seed = 42): boolean {
  if (x >= 144 && x <= 528 && Math.abs(z + 120) < 6 && !onCanalBridge(x, z)) return true;
  const d = districtAt(x, z); if (!d) return false;
  const cx = Math.floor(x / 48) * 48 + 24; const cz = Math.floor(z / 48) * 48 + 24;
  if (d.id === 'market-street') return [-1, 1].some(side => Math.abs(x - (cx + side * 12)) < 5.4 && Math.abs(z - (cz - 12)) < 4.9 || Math.abs(x - (cx + side * 12)) < 3.9 && Math.abs(z - (cz + 12)) < 3.4);
  if (d.id === 'flower-meadows') return [-12, 12].some(dx => [-12, 12].some(dz => Math.hypot(x - cx - dx, z - cz - dz) < .7));
  if (d.id === 'rainwater-canal') return canalBlocked(x, z, seed);
  return false;
}

export class Frontier {
  readonly cells = new Map<string, THREE.Group>();
  readonly animals: CanalAnimals;
  constructor(private parent: THREE.Group, private seed: number) { this.animals = new CanalAnimals(parent); }
  blocked(x: number, z: number): boolean { return frontierBlocked(x, z, this.seed); }
  stream(x: number, z: number): void {
    const keep = new Set<string>(); const cx = Math.floor(x / 48); const cz = Math.floor(z / 48);
    for (let a = cx - 1; a <= cx + 1; a++) for (let b = cz - 1; b <= cz + 1; b++) {
      if (!frontierCell(a, b)) continue;
      const key = `${a}:${b}`; keep.add(key); if (this.cells.has(key)) continue;
      const g = this.makeCell(a, b); this.parent.add(g); this.cells.set(key, g);
    }
    for (const [key, g] of this.cells) if (!keep.has(key)) {
      this.parent.remove(g); g.traverse(o => { if (o instanceof THREE.Mesh) { o.geometry.dispose(); if (o instanceof THREE.InstancedMesh) { (o.material as THREE.Material).dispose(); o.dispose(); } } }); this.cells.delete(key);
    }
  }
  private makeCell(cx: number, cz: number): THREE.Group {
    const g = new THREE.Group(); const x = cx * 48 + 24; const z = cz * 48 + 24; const d = districtAt(x, z)!;
    const floor = mesh(new THREE.PlaneGeometry(48, 48), d.id === 'flower-meadows' ? '#8faa7d' : d.id === 'market-street' ? '#c6c5b7' : '#859f94', g, x, -.02, z); floor.rotation.x = -Math.PI / 2; floor.castShadow = false;
    for (const angle of [0, Math.PI / 2]) { const path = mesh(new THREE.PlaneGeometry(48, 8), '#d2d9be', g, x, .015, z); path.rotation.set(-Math.PI / 2, 0, angle); path.castShadow = false; }
    const r = random((this.seed ^ cx * 73856093 ^ cz * 19349663) >>> 0);
    if (d.id === 'flower-meadows') {
      for (let i = 0; i < 4; i++) {
        const fx = x + (i % 2 ? 12 : -12); const fz = z + (i < 2 ? -12 : 12); const h = 12 + r() * 8;
        mesh(new THREE.CylinderGeometry(.18, .3, h, 8), '#59845c', g, fx, h / 2, fz);
        leaf(g, fx, h * .4, fz, 5, 1.2, '#699765', r() * 6);
        for (let p = 0; p < 6; p++) { const a = p * Math.PI / 3; ball(g, fx + Math.cos(a) * 1.6, h, fz + Math.sin(a) * 1.6, 1.6, .4, 1.1, i % 2 ? '#e7a7bd' : '#f1dd91'); }
        ball(g, fx, h + .2, fz, .65, .3, .65, '#d5aa51');
      }
      leaf(g, x + 5, .2, z + 2, 7, 2.5, '#dbabc2', .4);
      ball(g, x + 8, .4, z + 16, 3, .5, 2, '#a4d7d2');
    } else if (d.id === 'market-street') {
      for (const side of [-1, 1]) {
        const fx = x + side * 12;
        box(g, fx, .3, z - 12, 10, .6, 9, '#ba9973');
        for (let row = 0; row < 3; row++) for (const edge of [-1, 1]) { box(g, fx + edge * 5, 1 + row, z - 12, .3, .6, 9, '#c9aa80'); box(g, fx, 1 + row, z - 12 + edge * 4.5, 10, .6, .3, '#c9aa80'); }
        ball(g, fx, 3.5, z - 12, 3, 3, 3, side > 0 ? '#e7b580' : '#bcce80');
        box(g, fx, 3, z + 12, 7, 6, 6, '#d5c29d');
        box(g, fx, 1.8, z + 15.1, 3, 3.5, .05, '#8e8c72');
      }
    } else {
      buildCanal(g, x, z, cz === -3, this.seed);
    }
    for (const s of FRONTIER_SECRETS) if (Math.floor(s.x / 48) === cx && Math.floor(s.z / 48) === cz) {
      if (s.id === 'meadow-dew') ball(g, s.x, .3, s.z, 1.3, .3, 1, '#b5e4df');
      else if (s.id === 'meadow-petal') leaf(g, s.x + 3, .12, s.z + 3, 6, 2, '#e5b7c9', .5);
      else if (s.id === 'meadow-pollen') for (let i = 0; i < 4; i++) ball(g, s.x + i * .3, .2, s.z, .2, .2, .2, '#eed482');
      else if (s.id === 'market-button') mesh(new THREE.CylinderGeometry(.7, .7, .12, 24), '#9fb9c8', g, s.x, .12, s.z);
      else if (s.id === 'market-peach') ball(g, s.x + 4, 1.4, s.z + 3, 1.6, 1.5, 1.5, '#e8bc9c');
      else if (s.id === 'market-bag') { box(g, s.x + 4, 1.3, s.z + 3, 2, 2.6, 2, '#d5c29d'); box(g, s.x + 4, .9, s.z + 4.05, 1.2, 1.4, .03, '#89876e'); }
      else if (s.id === 'canal-shell') { ball(g, s.x, .4, s.z, 1, .4, .7, '#e2e3d9'); mesh(new THREE.TorusGeometry(.4, .045, 8, 24), '#9bb8b8', g, s.x, .65, s.z).rotation.x = -Math.PI / 2; }
      else if (s.id === 'canal-reeds') leaf(g, s.x + 2, .1, s.z + 1, 4, .8, '#94ae88', .4);
    }
    return g;
  }
}
